/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  TextField,
  Select,
  MenuItem,
  Button,
  Snackbar,
  Alert,
  IconButton,
  CircularProgress
} from '@mui/material';
import { Header } from 'components/home';
import { TopNavBar } from 'components/top_nav_bar';
import { ContainerForm } from 'components/shared/container_form';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useFormik } from 'formik';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useCuffaStore } from 'stores/cuffa/cuffa.store';
import { ROUTES } from 'routes/constants';
import { CuffaProductionDifficultyEnum } from 'interfaces/cuffa.interface';
import {
  difficultyApiKeys,
  reverseDifficultyApiKeys,
  desafiosPadrao,
  Prioridade,
  parseSavedProductionDifficulties,
  recomputePrioritiesOnSelect,
  changePriority,
  addOutroLogic,
  removeOutroLogic,
  buildProductionDifficultiesPayload,
} from './helpers';
import { CuffaAPI } from 'api/cuffa/cuffa.api';

// constants and pure helpers are provided by ./helpers

export default function DificuldadesProducao() {
  const navigate = useNavigate();
  const location = useLocation();
  const { uuid } = useParams<{ uuid: string }>();
  const { salvarDadosParciais } = useCuffaStore();

  const [initialLoading, setInitialLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' });
  const [mostrarOutroInput, setMostrarOutroInput] = useState(false);

  const formik = useFormik({
    initialValues: {
      desafiosSelecionados: [] as string[],
      prioridades: [] as { value: string; priority: number }[],
      outrosDesafios: [] as string[],
      novoOutro: '',
    },
    validationSchema: Yup.object().shape({}),
    onSubmit: (values) => {
      const { novoOutro, ...dadosParaSalvar } = values;
      salvarDadosParciais({
        dificuldadesProducao: dadosParaSalvar,
        ultimaRota: location.pathname,
      });
      setSnackbar({ open: true, message: 'Progresso salvo localmente!', severity: 'info' });
    },
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!uuid) {
        setSnackbar({ open: true, message: 'UUID da coleta não encontrado.', severity: 'error' });
        setInitialLoading(false);
        return;
      }
      try {
        // Primeiro tenta restaurar do localStorage: per-collection, depois global
        try {
          const storageKeys: string[] = [];
          if (uuid) storageKeys.push(`cuffa-storage-${uuid}`);
          storageKeys.push('cuffa-storage');

          for (const storageKey of storageKeys) {
            try {
              const raw = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
              if (!raw) continue;
              const parsed = JSON.parse(raw);
              const saved = parsed?.state?.dadosParciais?.dificuldadesProducao ?? parsed?.state?.dadosParciais?.dificuldades_producao;
              if (!saved) continue;

              const novosSelecionados: string[] = Array.isArray(saved.desafiosSelecionados)
                ? saved.desafiosSelecionados
                : Array.isArray(saved.selectedChallenges)
                  ? saved.selectedChallenges
                  : [];

              const novasPrioridades: { value: string; priority: number }[] = Array.isArray(saved.prioridades)
                ? saved.prioridades
                : Array.isArray(saved.priorities)
                  ? saved.priorities
                  : [];

              const novosOutros: string[] = Array.isArray(saved.outrosDesafios)
                ? saved.outrosDesafios
                : Array.isArray(saved.others)
                  ? saved.others
                  : [];

              formik.setValues({
                desafiosSelecionados: novosSelecionados,
                prioridades: novasPrioridades,
                outrosDesafios: novosOutros,
                novoOutro: ''
              });

              setInitialLoading(false);
              return;
            } catch (e) {
              console.warn(`Falha ao parsear ${storageKey} ao restaurar dificuldadesProducao:`, e);
              continue;
            }
          }
        } catch (e) {
          console.warn('Erro ao acessar localStorage cuffa-storage ao restaurar dificuldadesProducao', e);
        }

        // Se não encontrou no storage, busca pela API
        const response = await CuffaAPI.getDataByID(uuid);
        const savedData = response.dados?.production_difficulties;
        if (savedData) {
          const { novosSelecionados, novasPrioridades, novosOutros } = parseSavedProductionDifficulties(savedData);
          formik.setValues({
            desafiosSelecionados: novosSelecionados,
            prioridades: novasPrioridades,
            outrosDesafios: novosOutros,
            novoOutro: ''
          });
        }
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
        setSnackbar({ open: true, message: 'Falha ao carregar dados. Tente novamente.', severity: 'error' });
      } finally {
        setInitialLoading(false);
      }
    };
    fetchInitialData();
  }, [uuid]);

  

  const handleSelecionar = (nome: string) => {
    const { desafiosSelecionados, prioridades } = formik.values as {
      desafiosSelecionados: string[];
      prioridades: Prioridade[];
    };
    const { novosSelecionados, novasPrioridades } = recomputePrioritiesOnSelect(desafiosSelecionados, prioridades, nome);
    formik.setFieldValue('desafiosSelecionados', novosSelecionados);
    formik.setFieldValue('prioridades', novasPrioridades);
  };


  const handlePriorityChange = (value: string, novaPrioridade: number) => {
    const { prioridades } = formik.values;
    const novasPrioridades = changePriority(prioridades, value, novaPrioridade);
    formik.setFieldValue('prioridades', novasPrioridades);
  };

  // --- MODIFICAÇÃO: Atribui prioridade automática ao adicionar "Outro" ---
  const adicionarOutroDesafio = () => {
    const result = addOutroLogic(formik.values.novoOutro, desafiosPadrao, formik.values.outrosDesafios, formik.values.desafiosSelecionados, formik.values.prioridades);
    if (!result.ok) {
      setSnackbar({ open: true, message: result.message || 'Erro ao adicionar desafio.', severity: 'error' });
      return;
    }
    formik.setFieldValue('outrosDesafios', result.novosOutros);
    formik.setFieldValue('desafiosSelecionados', result.novosSelecionados);
    formik.setFieldValue('prioridades', result.novasPrioridades);
    formik.setFieldValue('novoOutro', '');
    setMostrarOutroInput(false);
  };


  const removerOutroDesafio = (desafioRemovido: string) => {
    const { outrosDesafios, desafiosSelecionados, prioridades } = formik.values as {
      outrosDesafios: string[];
      desafiosSelecionados: string[];
      prioridades: Prioridade[];
    };
    const { novosOutros, novosSelecionados, novasPrioridadesReajustadas } = removeOutroLogic(desafioRemovido, outrosDesafios, desafiosSelecionados, prioridades);
    formik.setFieldValue('outrosDesafios', novosOutros);
    formik.setFieldValue('desafiosSelecionados', novosSelecionados);
    formik.setFieldValue('prioridades', novasPrioridadesReajustadas);
  };


  const handleFinalizarColeta = async () => {
    if (!uuid) {
      setSnackbar({ open: true, message: 'Erro: UUID da coleta não encontrado.', severity: 'error' });
      return;
    }
    const { desafiosSelecionados, prioridades, outrosDesafios } = formik.values;
    if (desafiosSelecionados.length === 0) {
      setSnackbar({ open: true, message: 'Selecione pelo menos um desafio.', severity: 'error' });
      return;
    }
    const prioridadesDefinidas = prioridades.filter(p => p.priority > 0);
    if (prioridadesDefinidas.length !== desafiosSelecionados.length) {
      setSnackbar({ open: true, message: 'Defina uma prioridade para cada desafio selecionado.', severity: 'error' });
      return;
    }
    if (new Set(prioridadesDefinidas.map(p => p.priority)).size !== desafiosSelecionados.length) {
      setSnackbar({ open: true, message: 'As prioridades devem ser únicas para cada desafio.', severity: 'error' });
      return;
    }

    setSaving(true);
    const payload: any = buildProductionDifficultiesPayload(prioridades, outrosDesafios);

    try {
      await CuffaAPI.updateProductionDifficulties(uuid, payload);
      setSnackbar({ open: true, message: 'Dados de dificuldades salvos com sucesso!', severity: 'success' });
      setTimeout(() => navigate(ROUTES.CUFFA_ASSINATURA_INCLUIR.replace(':uuid', uuid)), 1500);
    } catch (error) {
      console.error('Erro ao salvar os dados:', error);
      setSnackbar({ open: true, message: 'Falha ao enviar os dados. Tente novamente.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const todosDesafiosParaExibir = [...desafiosPadrao, ...formik.values.outrosDesafios];

  if (initialLoading) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Dificuldades à Produção" showBackButton />
        <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
          <CircularProgress />
          <Typography ml={2}>Carregando dados...</Typography>
        </Box>
      </MenuProvider>
    );
  }

  return (
    <MenuProvider>
      <TopNavBar />
      <Header title="Dificuldades à Produção da Unidade Familiar" showBackButton />
      <Box width="100%" display="flex" justifyContent="center" margin="2rem">
        <Box sx={{ maxWidth: '1152px', padding: '2rem', width: '100%' }}>
          <ContainerForm title="Desafios à produção da unidade familiar" width="100%">
            <Box mb={3}>
              <Typography fontWeight="bold" mb={1}>Indique as principais dificuldades à produção da unidade familiar:</Typography>
              <Typography fontSize="14px">A prioridade será preenchida automaticamente. Se necessário, você pode ajustá-la.</Typography>
            </Box>
            <Box display="flex" flexDirection="column" gap={1}>
              {todosDesafiosParaExibir.map((nome) => {
                const isCustom = formik.values.outrosDesafios.includes(nome);
                return (
                  <Box key={nome} display="flex" justifyContent="space-between" alignItems="center" py={0.5}>
                    <Box display="flex" alignItems="center" gap={isCustom ? 1 : 2} sx={{ flex: 1 }}>
                      <Checkbox
                        checked={formik.values.desafiosSelecionados.includes(nome)}
                        onChange={() => handleSelecionar(nome)}
                      />
                      <Typography>{nome}</Typography>
                      {isCustom && (
                        <IconButton size="small" onClick={() => removerOutroDesafio(nome)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    {formik.values.desafiosSelecionados.includes(nome) && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography fontSize="14px">Prioridade:</Typography>
                        <Select
                          size="small"
                          sx={{ width: 80 }}
                          value={formik.values.prioridades.find((p) => p.value === nome)?.priority ?? ''}
                          onChange={(e) => handlePriorityChange(nome, Number(e.target.value))}
                        >
                          {Array.from({ length: formik.values.desafiosSelecionados.length }, (_, i) => i + 1).map((num) => (
                            <MenuItem key={num} value={num}>{num}</MenuItem>
                          ))}
                        </Select>
                      </Box>
                    )}
                  </Box>
                )
              })}
            </Box>
            {!mostrarOutroInput ? (
              <Box sx={{ border: '1px dashed #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.75rem', marginTop: '1rem', cursor: 'pointer' }}
                onClick={() => setMostrarOutroInput(true)}
              >
                <AddIcon sx={{ marginRight: '0.5rem', fontSize: '1rem' }} />
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 500, color: '#1a1a1a' }}>
                  Adicionar Outro Desafio
                </Typography>
              </Box>
            ) : (
              <Box mt={2} display="flex" alignItems="center" gap={1}>
                <TextField
                  fullWidth
                  label="Descreva o novo desafio"
                  name="novoOutro"
                  value={formik.values.novoOutro.toLocaleUpperCase()}
                  onChange={formik.handleChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      adicionarOutroDesafio();
                    }
                  }}
                />
                <IconButton color="primary" onClick={adicionarOutroDesafio} disabled={!formik.values.novoOutro.trim()}>
                  <AddIcon />
                </IconButton>
              </Box>
            )}
            <Box mt={4} display="flex" justifyContent="center" gap={2}>
              <Button variant="outlined" color="secondary" onClick={() => navigate(ROUTES.PARTICIPACAO_FEMININA_CUFFA_INCLUIR.replace(':uuid', uuid ?? ''))} disabled={saving}>Retornar</Button>
              <Button variant="outlined" color="secondary" onClick={() => formik.handleSubmit()} disabled={saving}>Salvar Progresso</Button>
              <Button variant="contained" color="primary" onClick={handleFinalizarColeta} disabled={saving}>
                {saving ? <CircularProgress size={24} color="inherit" /> : 'Salvar e Finalizar'}
              </Button>
            </Box>
          </ContainerForm>
        </Box>
      </Box>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MenuProvider>
  );
}
